import React, { useCallback, useContext, useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Link, useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { getURPEndDate, isPrecedingTerm } from '@ourbranch/policy-utils';
import { getTodayStr } from '@ourbranch/date-fns-helpers';

import { useStore } from 'core/store';
import { PolicyTabs } from 'core/store/account-store/policies-store/policy-store/policy-store';
import { get, getUmbrellaSuffix, policyTypes } from 'core/helpers/lookups';
import { awsDateToStringDate } from 'core/helpers/formatters';
import { showExpirationInsteadOfEndDate, endDateToExpirationDisplay } from 'core/helpers/expiration-date';
import {
  PolicyStatus,
  getPolicyStatusLabel,
  isImportedPolicy,
  isImportedAndNotEndorseable
} from 'core/helpers/policy-status';
import { showsLastInspectionField } from 'core/helpers/policy-type';
import { resolveLastInspectionDate } from 'core/helpers/last-inspection';
import { getRenewalGenerationWindow } from 'core/helpers/renewal-generation-window';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import LabelWithCopyButton from 'core/components/label-with-copy-button';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { AuthContext } from 'core/components/auth';
import classNames from 'classnames';
import { Tag } from 'core/components/tag';
import { Tooltip } from 'core/components/tooltip';
import backIcon from 'core/components/screen-header/go-to.svg';
import navigateIcon from 'core/assets/svg/ic-navigate.svg';
import agencyIcon from 'customer/assets/icSold.svg';
import FrontContact from 'customer/components/front-contact/front-contact';
import { isCondensedLayout } from 'core/components/theme/condensed-layout';

import useStyles from './policy-header.styles';

const getPolicyType = get(policyTypes);

const POLICY_SOURCE_LABELS = {
  'imt-conversion': 'IMT import',
  'guidewire-conversion': 'Guidewire import'
};

const getPolicySourceLabel = (source) => POLICY_SOURCE_LABELS[source] || 'Created in Staff';

const PolicyHeader = ({ history }) => {
  const store = useStore();
  const { account: accountStore } = store;
  const policy = accountStore?.policies?.policy?.policy;
  const { policyId, id } = useParams();
  const accountId = accountStore.id || id;
  const { isExternalSales, isAgency, canEditPolicies, viewOnly } = useContext(AuthContext);
  const policyStatus = policy ? (accountStore?.policies?.policy?.status ?? null) : null;
  const classes = useStyles();
  // In the condensed workspace the customer header above names the customer, links back and has Front.
  const condensed = isCondensedLayout();

  const isFarm = isFarmBasedPolicyType(policy?.policyType);

  // #33158: most recent inspection date (stored Policy.lastInspectionDate vs the live completed
  // inspection for this term). Internal-only; shown on non-auto policies when a date exists.
  const lastInspectionDisplay = resolveLastInspectionDate({
    storedDate: policy?.lastInspectionDate,
    inspectionDateCompleted: accountStore?.policies?.policy?.inspection?.dateCompleted
  });
  const containerRef = useRef(null);
  const buttonContainerRef = useRef(null);

  useEffect(() => {
    // The condensed layout has no back button, and its navigator button sits inline, so nothing to position.
    if (isCondensedLayout()) {
      return undefined;
    }
    const updatePosition = () => {
      if (containerRef.current && buttonContainerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        buttonContainerRef.current.style.transform = `translateX(${rect.left - 64}px)`;
      }
    };

    let timeoutId = null;
    const debouncedUpdatePosition = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(updatePosition, 30);
    };

    updatePosition(); // Initial call
    window.addEventListener('resize', debouncedUpdatePosition);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', debouncedUpdatePosition);
    };
  }, []);

  const titleText = () => {
    if (!(policy?.id && policy?.id === policyId)) {
      if (condensed) {
        return 'Loading...';
      }
      return accountStore.accountLoading ? 'Loading...' : `${accountStore.fname} ${accountStore.lname}/Loading...`;
    }
    const umbrellaSuffix = getUmbrellaSuffix(accountStore?.policies?.policy);
    if (condensed) {
      return `${getPolicyType(policy.policyType)}${umbrellaSuffix} policy`;
    }
    return `${accountStore.fname} ${accountStore.lname}/${getPolicyType(policy.policyType)}${umbrellaSuffix} Policy`;
  };

  const title = titleText();

  // Term dates mirror the Settings "Policy Term" panel: effectiveDate (start) and
  // endDate (last full day of coverage, stored directly with no date math).
  // awsDateToStringDate parses the AWS "yyyy-MM-dd" string via parseISO (local time),
  // avoiding the UTC-midnight off-by-one that `new Date(awsString)` causes in US timezones.
  // For brands with showExpirationDateInsteadOfEndDate, display the expiration date (endDate + 1)
  // instead — except on a flat cancellation (endDate === effectiveDate, never in force). (#32070)
  const isFlatCancel = policy?.endDate && policy?.endDate === policy?.effectiveDate;
  const displayEndDate =
    showExpirationInsteadOfEndDate() && !isFlatCancel ? endDateToExpirationDisplay(policy?.endDate) : policy?.endDate;
  const termDates =
    policy?.effectiveDate && policy?.endDate
      ? `${awsDateToStringDate(policy.effectiveDate)} - ${awsDateToStringDate(displayEndDate)}`
      : null;

  // URP only applies to new business (first term). Null for states/types with no URP, incl. all farm. (#34196)
  const urpEndDate = policy?.term === 1 && policy?.effectiveDate ? getURPEndDate(policy) : null;
  // The account's policy list holds every term, so the renewal exists when the next term is in it.
  // Match on account + line + term, not the full id: a BIX conversion renews `-001-SC` as `-002-BX`.
  // Once it exists, link to it; until then, show when the renewals cron will generate it. (#34196)
  const renewalPolicyId = policy?.id
    ? (accountStore?.policies?.list?.find((accountPolicy) => isPrecedingTerm(policy.id, accountPolicy.id))?.id ?? null)
    : null;
  // Expired, cancelled and in-cancellation terms will not renew, so no window for them.
  const renewalGenerationWindow =
    !renewalPolicyId && [PolicyStatus.Active, PolicyStatus.Future].includes(policyStatus)
      ? getRenewalGenerationWindow(policy)
      : null;
  // Past the last retry day and still no renewal in the list: say it was not generated, instead of showing past dates.
  // yyyy-MM-dd strings compare in date order; "today" is in the policy's state timezone.
  const renewalWindowEnded = renewalGenerationWindow && renewalGenerationWindow.to < getTodayStr(policy.state);

  // Condensed: Details is read-only and every edit goes through the drawer, so the button follows the rules that
  // disable the Details form (hold cards, cancelled, no edit permission) and hide the footer.
  const policyStore = accountStore?.policies?.policy;
  const canMakeChange =
    condensed &&
    policy?.id === policyId &&
    canEditPolicies &&
    !viewOnly &&
    !policyStore.allActiveHoldCards().length &&
    ![PolicyStatus.InCancellation, PolicyStatus.Cancelled].includes(policyStatus) &&
    policy.canEndorse !== false &&
    !isImportedAndNotEndorseable(policy);

  const onBack = useCallback(() => {
    history.push(`/customer/${accountId}`);
  }, [accountId, history]);

  const statusPill = policyStatus && (
    <div
      className={classNames(classes.statusPillBase, {
        [classes.statusPillActive]: policyStatus === PolicyStatus.Active,
        [classes.statusPillFuture]: policyStatus === PolicyStatus.Future,
        [classes.statusPillInCancellation]: policyStatus === PolicyStatus.InCancellation,
        [classes.statusPillCancelled]: policyStatus === PolicyStatus.Cancelled,
        [classes.statusPillInactive]: policyStatus === PolicyStatus.Inactive,
        [classes.condensedStatusPill]: condensed
      })}
    >
      {getPolicyStatusLabel(policy, policyStatus)}
    </div>
  );
  const navigatorButton = isFarm && (
    <IconButtonWithTooltip
      tooltip="Navigate this policy"
      icon={navigateIcon}
      onClick={() => {
        // set the current tab to DETAILS
        accountStore.policies.policy.setCurrentTab(PolicyTabs.DETAILS);
        accountStore.policies.policy.setFarmNavigatorOpen(true);
      }}
      iconClassName={classes.icon}
      imgClassName={classes.navigatorIconImg}
      placement="right"
    />
  );
  const showSource = isFarm || isImportedPolicy(policy);
  const priorPolicyId = policy?.importedData?.conversionMetadata?.policyNumber;
  const showLastInspection = !isAgency && showsLastInspectionField(policy?.policyType) && lastInspectionDisplay;
  const sourceTooltip = (
    <Tooltip
      className={classes.sourceTooltipIcon}
      text="Source indicates whether this policy was imported from a previous system or created directly in Staff."
      placement="bottom"
      smallIcon={{}}
    />
  );
  const renewalLink = renewalPolicyId && (
    <Link to={`/customer/${accountId}/policy/${renewalPolicyId}`}>{renewalPolicyId}</Link>
  );
  const renewalWindowText =
    renewalGenerationWindow &&
    (renewalWindowEnded
      ? `Not generated, window ended ${awsDateToStringDate(renewalGenerationWindow.to)}`
      : `from ${awsDateToStringDate(renewalGenerationWindow.from)} to ${awsDateToStringDate(renewalGenerationWindow.to)}`);
  const agencyTag = policy?.isAgentSold && !isExternalSales && (
    <Tag className={classes.agencyTag}>
      <img alt="agency sold icon" src={agencyIcon} />
      Agency policy
    </Tag>
  );

  // Condensed: one row (title, status, ID, Make a change), then the facts in a wrapping grid, as in the prototype.
  if (condensed) {
    const facts = [
      termDates && { key: 'term', label: 'Term dates', value: termDates },
      urpEndDate && { key: 'urp', label: 'End of URP', value: awsDateToStringDate(urpEndDate) },
      showLastInspection && { key: 'inspection', label: 'Last inspection', value: lastInspectionDisplay },
      showSource && {
        key: 'source',
        label: (
          <>
            Source
            {sourceTooltip}
          </>
        ),
        value: getPolicySourceLabel(policy?.source)
      },
      showSource && priorPolicyId && { key: 'prior', label: 'Prior policy ID', value: priorPolicyId },
      renewalPolicyId && { key: 'renewal', label: 'Renewal', value: renewalLink },
      renewalGenerationWindow && {
        key: 'renewal-window',
        label: renewalWindowEnded ? 'Renewal' : 'Renewal generates',
        value: renewalWindowText
      }
    ].filter(Boolean);

    return (
      <div className={classes.condensedHeader}>
        <div className={classes.titleRow} data-testid="policy-header-title-row">
          <div className={classes.titleGroup}>
            {navigatorButton}
            <Label type="subtitle" component="h2" className={classes.condensedTitle}>
              {title}
            </Label>
            {statusPill}
            <LabelWithCopyButton type="body3Bold">{policyId}</LabelWithCopyButton>
          </div>
          {canMakeChange && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => policyStore.setMakeChangeOpen(true)}
              data-cy="make-a-change"
            >
              Make a change
            </Button>
          )}
        </div>
        {(facts.length > 0 || agencyTag) && (
          <div className={classes.factsRow} data-testid="policy-header-facts">
            <dl className={classes.facts}>
              {facts.map(({ key, label, value }) => (
                <div key={key}>
                  <dt className={classes.factLabel}>{label}</dt>
                  <dd className={classes.factValue}>{value}</dd>
                </div>
              ))}
            </dl>
            {agencyTag}
          </div>
        )}
      </div>
    );
  }

  return (
    <Grid container className={classes.header} justifyContent="space-between">
      <Grid item className={classes.container} ref={containerRef}>
        <div className={classes.fixedButtonContainer} ref={buttonContainerRef}>
          <IconButtonWithTooltip
            tooltip="Go back to previous page"
            icon={backIcon}
            onClick={onBack}
            iconClassName={classes.icon}
            imgClassName={classes.iconImg}
            placement="right"
          />
          {navigatorButton}
        </div>
        <Label type="title">{title}</Label>
        {!isExternalSales && <FrontContact />}
      </Grid>
      <Grid item className={classes.rightContainer}>
        <>
          {agencyTag && <div className={classes.rightSideInfo}>{agencyTag}</div>}
          <div className={classes.rightSideInfo}>
            <Label type="infoLabel" className={classes.rightLabel}>
              PolicyID
            </Label>
            <LabelWithCopyButton type="body3Bold">{policyId}</LabelWithCopyButton>
          </div>
          {showSource && (
            <>
              <div className={classes.rightSideInfo}>
                <Label type="infoLabel" className={classes.sourceLabel}>
                  Source
                </Label>
                {sourceTooltip}
                <Label type="infoValue" className={classes.rightValue}>
                  {getPolicySourceLabel(policy?.source)}
                </Label>
              </div>
              {priorPolicyId && (
                <div className={classes.rightSideInfo}>
                  <Label type="infoLabel" className={classes.rightLabel}>
                    Prior Policy ID
                  </Label>
                  <Label type="infoValue" className={classes.rightValue}>
                    {priorPolicyId}
                  </Label>
                </div>
              )}
            </>
          )}
          {showLastInspection && (
            <div className={classes.rightSideInfo}>
              <Label type="infoLabel" className={classes.rightLabel}>
                Last Inspection
              </Label>
              <Label type="infoValue" className={classes.rightValue}>
                {lastInspectionDisplay}
              </Label>
            </div>
          )}
          {urpEndDate && (
            <div className={classes.rightSideInfo}>
              <Label type="infoLabel" className={classes.rightLabel}>
                End of URP
              </Label>
              <Label type="infoValue" className={classes.rightValue}>
                {awsDateToStringDate(urpEndDate)}
              </Label>
            </div>
          )}
          {renewalPolicyId && (
            <div className={classes.rightSideInfo}>
              <Label type="infoLabel" className={classes.rightLabel}>
                Renewal
              </Label>
              <Label type="infoValue" className={classes.rightValue}>
                {renewalLink}
              </Label>
            </div>
          )}
          {renewalGenerationWindow && (
            <div className={classes.rightSideInfo}>
              <Label type="infoLabel" className={classes.rightLabel}>
                {renewalWindowEnded ? 'Renewal' : 'Renewal Generates'}
              </Label>
              <Label type="infoValue" className={classes.rightValue}>
                {renewalWindowText}
              </Label>
            </div>
          )}
          {termDates && (
            <div className={classes.rightSideInfo}>
              <Label type="infoLabel" className={classes.rightLabel}>
                Term Dates
              </Label>
              <Label type="infoValue" className={classes.rightValue}>
                {termDates}
              </Label>
              {statusPill}
            </div>
          )}
        </>
      </Grid>
    </Grid>
  );
};

export default observer(PolicyHeader);
